import { useGetClientSettingMapView } from 'hooks/map-view/useGetClientSettingMapView';
import { useClientData } from 'hooks/useClientData';
import useClientDataConfig from 'hooks/useClientDataConfig';
import { useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  changeFixedSizePoint,
  changeZoomLayerCircum,
  circumferenceAnalyticsSelector,
} from 'store/slices/map-view/circumferenceAnalytics';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import EditTabLayer from './EditTabLayer';

export const useCircumferenceAnalytics = ({ mapRef }: any) => {
  const { isLayer3D, isDisplayBoundaryLine, isDisplay3DCylinder, isInEditTab, clickedStandPointId } =
    useAppSelector(mapViewSelector);
  const { dataGeoJsonPoint, dataGeoJsonPolygon } = useAppSelector(circumferenceAnalyticsSelector);
  const { getTopSurfaceOpacity, colorTransparent, isSameColorTopSurface, getOpacityExtrusion } =
    useGetClientSettingMapView();
  const { contourSetting } = useClientData();
  const { isFixedPoint, value } = useClientDataConfig();
  const dispatch = useAppDispatch();

  const zoomLevel = useMemo(() => {
    if (!mapRef?.current) return 0;
    return mapRef?.current?.getZoom();
  }, [mapRef?.current?.getZoom()]);

  useEffect(() => {
    if (!mapRef?.current) return;

    if (isFixedPoint && value) {
      dispatch(changeFixedSizePoint(value));
    } else dispatch(changeZoomLayerCircum(zoomLevel));
  }, [dispatch, isFixedPoint, mapRef, value, zoomLevel]);

  const layerCircumference = useMemo(() => {
    if (!dataGeoJsonPoint && !dataGeoJsonPolygon) {
      return null;
    }
    return (
      <>
        <EditTabLayer />
        {dataGeoJsonPoint && !isInEditTab && (
          <Source id={`circumference-3d-cylinder`} type="geojson" data={dataGeoJsonPoint}>
            {/* 2D Point */}
            <Layer
              id={`fill-top-surface-point`}
              type="fill"
              source={`circumference-circle-point`}
              paint={{
                'fill-color': ['get', 'color'],
              }}
              layout={{ visibility: !isLayer3D && !isDisplayBoundaryLine ? 'visible' : 'none' }}
            />
            {/* 3D Cylinder */}
            <Layer
              id={`circumference-top-surface-point`}
              type="fill-extrusion"
              source={`circumference-circle-point`}
              filter={['!=', 'color', 'transparent']}
              paint={{
                'fill-extrusion-color': ['get', 'color'],
                'fill-extrusion-height': contourSetting,
                'fill-extrusion-base': contourSetting,
                'fill-extrusion-opacity': getTopSurfaceOpacity,
                'fill-extrusion-vertical-gradient': false,
              }}
              layout={{ visibility: isLayer3D && isDisplay3DCylinder ? 'visible' : 'none' }}
            />
            <Layer
              id={`circumference-side-surface-point`}
              type="fill-extrusion"
              source={`circumference-circle-point`}
              filter={['!=', 'color', 'transparent']}
              paint={{
                'fill-extrusion-color': isSameColorTopSurface ? ['get', 'color'] : colorTransparent,
                'fill-extrusion-height': contourSetting,
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': getOpacityExtrusion,
                // getOpacityExtrusion,
                'fill-extrusion-vertical-gradient': false,
              }}
              layout={{ visibility: isLayer3D && isDisplay3DCylinder ? 'visible' : 'none' }}
            />
          </Source>
        )}
        {dataGeoJsonPolygon && !isInEditTab && (
          <Source id={`circumference-source`} type="geojson" data={dataGeoJsonPolygon}>
            {/* 2D Contour */}
            <Layer
              id={`circumference_polygon`}
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 2,
                'line-dasharray': [2, 2],
              }}
              source={`circumference-source`}
              layout={{ visibility: !isLayer3D && isDisplayBoundaryLine ? 'visible' : 'none' }}
            />
            {/* 3D Filled contour */}
            <Layer
              id={`circumference_fill-polygon`}
              type="fill"
              paint={{
                'fill-color': ['get', 'color'],
                'fill-opacity': 0,
              }}
              source={`circumference-source`}
              layout={{ visibility: isLayer3D && !isDisplay3DCylinder ? 'visible' : 'none' }}
            />
            <Layer
              id={`circumference_top-surface`}
              type="fill-extrusion"
              beforeId={`circumference_fill-polygon`}
              source={`circumference_source`}
              filter={['!=', 'color', 'transparent']}
              paint={{
                'fill-extrusion-color': ['get', 'color'],
                'fill-extrusion-height': contourSetting,
                'fill-extrusion-base': contourSetting,
                'fill-extrusion-opacity': getTopSurfaceOpacity,
                'fill-extrusion-vertical-gradient': false,
              }}
              layout={{ visibility: isLayer3D && !isDisplay3DCylinder ? 'visible' : 'none' }}
            />
            <Layer
              id={`circumference_side-surface`}
              type="fill-extrusion"
              beforeId={`circumference_top-surface`}
              source={`circumference_source`}
              filter={['!=', 'color', 'transparent']}
              paint={{
                'fill-extrusion-color': isSameColorTopSurface ? ['get', 'color'] : colorTransparent,
                'fill-extrusion-height': contourSetting,
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': getOpacityExtrusion,
                'fill-extrusion-vertical-gradient': false,
              }}
              layout={{ visibility: isLayer3D && !isDisplay3DCylinder ? 'visible' : 'none' }}
            />
          </Source>
        )}
      </>
    );
  }, [
    colorTransparent,
    contourSetting,
    dataGeoJsonPoint,
    dataGeoJsonPolygon,
    getOpacityExtrusion,
    getTopSurfaceOpacity,
    isDisplay3DCylinder,
    isDisplayBoundaryLine,
    isInEditTab,
    isLayer3D,
    isSameColorTopSurface,
  ]);
  return { layerCircumference };
};
